import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { Box, LinearProgress, Paper, Stack, Typography, alpha } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { getReadinessMeasureMeta } from '../../core/diagnostic'

export default function ReadinessMeasures({ fishbone }) {
  const { t } = useTranslation()
  const bones = fishbone?.bones || []
  const compliantCount = bones.filter((bone) => bone.status === 'implemented').length
  const complianceRatio = bones.length ? Math.round((compliantCount / bones.length) * 100) : 0

  return (
    <Paper variant="outlined" sx={{ p: 2.5, bgcolor: 'background.paper' }}>
      <Stack spacing={2.5}>
        <Typography variant="h5">{t('report.readinessGaps')}</Typography>
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
          <Stack spacing={1.25}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Typography variant="body2">
                {t('report.measuresCompliant', { count: compliantCount, total: bones.length })}
              </Typography>
              <Typography variant="body2">{complianceRatio}%</Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={complianceRatio}
              color={complianceRatio >= 100 ? 'success' : 'error'}
              sx={{
                height: 6,
                borderRadius: 999,
                bgcolor: (theme) => alpha(theme.palette.text.primary, 0.08),
              }}
            />
          </Stack>
        </Paper>
        <Stack spacing={0}>
          {bones.map((bone) => {
            const gap = bone.status === 'not_implemented' || bone.status === 'unsure'
            const compliant = bone.status === 'implemented'
            const meta = getReadinessMeasureMeta(bone.id)
            const statusKey = compliant ? 'compliant' : bone.status === 'partially_implemented' ? 'partial' : 'gap'

            return (
              <Stack
                key={bone.id}
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                spacing={2}
                sx={{
                  px: 0,
                  py: 1.5,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="flex-start" flexGrow={1}>
                  {compliant ? (
                    <CheckCircleIcon color="success" sx={{ mt: 0.15, fontSize: 18 }} />
                  ) : (
                    <WarningAmberIcon color={gap ? 'error' : 'warning'} sx={{ mt: 0.15, fontSize: 18 }} />
                  )}
                  <Box>
                    <Typography variant="body2">
                      {t(`articles.${meta.key}.title`, { defaultValue: bone.label })}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                      {t(`articles.${meta.key}.description`)}
                    </Typography>
                  </Box>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.08em', pt: 0.25 }}>
                  {t(`report.status.${statusKey}`)}
                </Typography>
              </Stack>
            )
          })}
        </Stack>
      </Stack>
    </Paper>
  )
}
